import type { AttachmentRow, InvoiceDetail } from "./detail-data";
import { t } from "../../../lib/sprache";

/**
 * Versand an die Marke und Mahnung (Korrekturrunde 1, PARITAET-2 und PARITAET-3): wer in Kopie steht und welche Belege
 * mitgehen können. Ohne Abhängigkeit von React, damit Seite und Tests dieselbe Regel nutzen.
 */

const MAIL = /^[^@\s"<>,;]+@[^@\s"<>,;]+\.[^@\s"<>,;]+$/;

/**
 * Kopie-Empfänger aus den Kontakten der Marke mit der Rolle „Rechnung in Kopie“ (invoice_cc). Vorbelegung für Senden,
 * Erneut senden und Mahnung; der Empfänger selbst steht nicht zusätzlich in Kopie. Rechnungen an den Creator: keine.
 */
export function invoiceCcOf(d: Pick<InvoiceDetail, "invoice" | "contacts">, recipient: string): string[] {
  if (d.invoice.kind === "agency_to_talent") return [];
  const to = recipient.trim().toLowerCase();
  const out: string[] = [];
  for (const c of d.contacts) {
    const mail = c.email?.trim().toLowerCase();
    if (!mail || !MAIL.test(mail) || mail === to || out.includes(mail)) continue;
    if ((c.roles ?? []).includes("invoice_cc")) out.push(mail);
  }
  return out;
}

/** Kopie-Feld: Adressen mit Komma, Semikolon oder Leerzeichen getrennt */
export function parseCc(text: string): string[] {
  return [...new Set(text.split(/[,;\s]+/).map((x) => x.trim().toLowerCase()).filter(Boolean))];
}

export interface SendableFile { id: string; name: string; sizeBytes: number; source: "own" | "creator"; preselected: boolean }

/**
 * Anhänge, die mit der Rechnungsmail an die Marke gehen können: eigene Anlagen der Rechnung (ohne das Rechnungs-PDF selbst,
 * vorausgewählt) und Belege der Creator-Rechnungen zum selben Monat bzw. zur selben Kooperation (nie deren Rechnungsdokument;
 * nicht vorausgewählt, die Agentur entscheidet). Gleiche Regel wie invoice_send_to_brand (0080).
 */
export function sendableAttachments(d: Pick<InvoiceDetail, "invoice" | "attachments" | "related"> & { relatedAttachments?: AttachmentRow[] }): SendableFile[] {
  const pdf = d.invoice.pdf_path as string | null;
  const own = d.attachments
    .filter((a) => a.storage_path !== pdf && a.category !== "invoice")
    .map((a): SendableFile => ({ id: a.id, name: a.file_name, sizeBytes: a.size_bytes, source: "own", preselected: a.category !== "dunning_letter" }));
  const creatorDocs = new Map(d.related.filter((r) => r.kind === "talent_to_agency" || r.kind === "talent_to_brand")
    .filter((r) => !["withdrawn", "discarded"].includes(r.status)).map((r) => [r.id, r.pdf_path as string | null]));
  const related = (d.relatedAttachments ?? [])
    .filter((a) => a.owner_id && creatorDocs.has(a.owner_id) && a.category !== "invoice" && a.storage_path !== creatorDocs.get(a.owner_id))
    .map((a): SendableFile => ({ id: a.id, name: a.file_name, sizeBytes: a.size_bytes, source: "creator", preselected: false }));
  return [...own, ...related];
}

export const MAX_SEND_FILES = 10;
export const MAX_SEND_BYTES = 20 * 1024 * 1024;

/** Prüfung der Auswahl vor dem Senden (wie die Datenbank): höchstens 10 Dateien, zusammen höchstens 20 MB */
export function selectionProblem(files: readonly SendableFile[], selected: ReadonlySet<string>): string | null {
  const chosen = files.filter((f) => selected.has(f.id));
  if (chosen.length > MAX_SEND_FILES) return t(`Höchstens ${MAX_SEND_FILES} Anhänge je Mail.`, `At most ${MAX_SEND_FILES} attachments per email.`);
  if (chosen.reduce((a, f) => a + f.sizeBytes, 0) > MAX_SEND_BYTES) return t("Die Anhänge sind zusammen größer als 20 MB. Bitte weniger oder kleinere Dateien wählen.", "The attachments are larger than 20 MB in total. Please choose fewer or smaller files.");
  return null;
}

/**
 * Was mit der Mahnung mitgeht (PRAXIS-R2-6): Übernommene Rechnungen ohne hochgeladenes Original haben kein Rechnungs-PDF; die
 * Mahnung geht dann nur mit dem Mahnschreiben (invoice_send_dunning hängt nur vorhandene Dateien an). Das sagt die Seite so.
 */
export function mahnAnhangText(o: { toCreator: boolean; attachLetter: boolean; noPdf: boolean }): string {
  if (o.toCreator) {
    return o.noPdf
      ? t("Die Mail geht an den Creator mit einem Link ins Portal, ohne Rechnungs-PDF (kein Original hinterlegt).", "The email goes to the creator with a link to the portal, without the invoice PDF (no original stored).")
      : t("Die Mail geht an den Creator mit dem Rechnungs-PDF und einem Link ins Portal.", "The email goes to the creator with the invoice PDF and a link to the portal.");
  }
  if (o.noPdf) {
    return o.attachLetter
      ? t("Die Mail geht mit dem Mahnschreiben im Anhang an die Marke, ohne Rechnungs-PDF: Für diese übernommene Rechnung ist kein Original hinterlegt.", "The email goes to the brand with the dunning letter attached, without the invoice PDF: no original is stored for this taken-over invoice.")
      : t("Die Mail geht ohne Anhang an die Marke: Für diese übernommene Rechnung ist kein Original hinterlegt. Tipp: Mahnschreiben anhängen oder das Original an der Rechnung hochladen.", "The email goes to the brand without attachments: no original is stored for this taken-over invoice. Tip: attach the dunning letter or upload the original on the invoice.");
  }
  return o.attachLetter
    ? t("Die Mail geht mit dem Rechnungs-PDF und dem Mahnschreiben im Anhang an die Marke.", "The email goes to the brand with the invoice PDF and the dunning letter attached.")
    : t("Die Mail geht mit dem Rechnungs-PDF im Anhang an die Marke.", "The email goes to the brand with the invoice PDF attached.");
}

/** Vorschau im Textfeld: Betreff und Absätze ohne Tags und ohne HTML-Entitäten („CHF 1'800.00“ statt „&#39;“, PRAXIS-R3-6) */
export function mahnVorschauText(preview: { subject: string; html: string }): string {
  const body = preview.html.replace(/<br\s*\/?>/gi, "\n").replace(/<\/p>/gi, "\n\n").replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, "\"").replace(/&#39;|&#x27;|&apos;/g, "'").replace(/&amp;/g, "&").replace(/\n{3,}/g, "\n\n").trim();
  return `${preview.subject}\n\n${body}`;
}

/**
 * Vorschau einer Mail als Klartext (PARITAET-R3-1): Betreff, Leerzeile, Text der Vorlage. Eine persönliche Nachricht steht
 * wie beim Versand (packages/server withPersonalMessage) nach dem ersten Absatz, also unter der Anrede und über dem
 * Vorlagentext, eingeleitet mit „Nachricht von <Agentur>:“ in der Sprache der Mail.
 */
export function mailPreviewText(mail: { subject: string; html: string }, message: string, agencyName: string, lang: "de" | "en"): string {
  const toText = (html: string) => html.replace(/<br\s*\/?>/gi, "\n").replace(/<\/p>/gi, "\n\n").replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, "\"").replace(/&#39;|&#x27;|&apos;/g, "'").replace(/&amp;/g, "&").trim();
  const note = message.trim();
  let html = mail.html;
  if (note) {
    const intro = lang === "en" ? `Message from ${agencyName}:` : `Nachricht von ${agencyName}:`;
    const block = `<p>${intro}\n${note.slice(0, 2000).replace(/</g, "‹").replace(/>/g, "›")}</p>`;
    const end = html.indexOf("</p>");
    html = end >= 0 ? html.slice(0, end + 4) + block + html.slice(end + 4) : block + html;
  }
  return `${mail.subject}\n\n${toText(html)}`.replace(/\n{3,}/g, "\n\n");
}
